<template>
  <div class="request-policies h-100">
    <div class="table-wrapper">
      <div class="table-header">
        <div class="table-header__title">
          <p class="text">
            Origin request policies
          </p>
          <p>
            If you want to add more information to the viewer request such as
            URL query strings, HTTP Headers, and cookies, that are not included
            by default in the origin request, you can use an origin request
            policy to control what information is included in an origin request.
          </p>
        </div>
        <span
          v-b-popover.hover.v-primary="'This feature will be available soon.'"
        >
          <b-button
            v-if="$can('create', 'originrequestspolicies', 'cdn')"
            disabled
            variant="bg-primary"
          >
            <GjIcon
              class="icon-left"
              name="Plus"
            />
            Create request policy
          </b-button>
        </span>
        <b-button
          v-if="$can('update', 'originrequestspolicies', 'cdn')"
          variant="bg-white"
          :disabled="isEditable"
          class="d-none d-lg-inline"
          @click="onEditClick"
        >
          Edit
        </b-button>
        <button-loading
          v-if="$can('delete', 'originrequestspolicies', 'cdn')"
          variant="bg-white"
          :spinning="removingRequestPolicy"
          :disabled="isEditable"
          class="d-none d-lg-inline"
          @click="onDeleteClick"
        >
          Delete
        </button-loading>
      </div>
      <div class="default-table d-none d-lg-block">
        <div class="position-relative">
          <b-table
            ref="table"
            :items="requestPolicies"
            :fields="fields"
            :class="{ loaded: !loading }"
            select-mode="single"
            show-empty
            selectable
            responsive
            @row-selected="onRowSelected"
          >
            <template #cell(selected)="{ rowSelected }">
              <select-button :selected="rowSelected" />
            </template>

            <template #cell(name)="data">
              <a
                class="clickable"
                @click="onNameClick(data.item.id)"
              >{{
                data.value
              }}</a>
            </template>

            <template #cell(lastModified)="data">
              {{ formatOffsetDateTime(data.value) }}
            </template>

            <template #cell(type)="data">
              <template v-if="data.item.isGjirafaPolicy">
                SYSTEM
              </template>
              <template v-else>
                CUSTOM
              </template>
            </template>

            <template #empty>
              <div
                v-if="loading"
                class="loading-table text-center py-3"
              >
                <b-spinner variant="primary" />
              </div>
              <template v-else>
                <no-data
                  no-data-text="No origin request policy have been added yet..."
                  access-page="Oirigin Request Policies"
                  :has-permissions="hasPermissions"
                />
              </template>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <!-- MOBILE -->
    <div class="position-relative d-lg-none">
      <div :class="{ 'opacity-7': loading }">
        <template v-for="policy in requestPolicies">
          <card
            :key="policy.id"
            :fields="fields"
            :item="policy"
            class="top-fade-in"
          >
            <template #options>
              <b-dropdown
                v-if="!policy.isGjirafaPolicy"
                id="dropdown-1"
                right
                variant="white"
                @show="select(policy)"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  v-if="$can('update', 'cachepolicies', 'cdn')"
                  @click="onEditClick"
                >
                  Edit
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can('delete', 'cachepolicies', 'cdn')"
                  @click="onDeleteClick"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>
            <template #name>
              <a
                class="link"
                @click="onNameClick(policy.id)"
              >{{
                policy.name
              }}</a>
            </template>
            <template #lastModified>
              {{ formatOffsetDateTime(policy.lastModified) }}
            </template>
            <template #type>
              <template v-if="policy.isGjirafaPolicy">
                SYSTEM
              </template>
              <template v-else>
                CUSTOM
              </template>
            </template>
          </card>
        </template>
      </div>
      <div
        v-if="loading"
        class="mobile-loading"
      >
        <table-busy />
      </div>
      <template v-if="!loading && requestPolicies.length < 1">
        <paper-simple class="top-fade-in">
          <no-data
            class="text-center"
            no-data-text="No origin request policy have been added yet..."
            access-page="Oirigin Request Policies"
            :has-permissions="hasPermissions"
          />
        </paper-simple>
      </template>
    </div>

    <info
      v-if="openInfo"
      :title="infoTitle"
      :message="infoMessage"
      @close="openInfo = false"
    />
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'
import messages from '../../messages'

export default {
  name: 'RequestPolicy',
  components: {
    Card,
  },
  data() {
    return {
      messages,
      openInfo: false,
      infoTitle: '',
      infoMessage: '',
      selected: [],
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        {
          key: 'name',
          label: 'name',
          variant: 'fill',
          isPrimary: true,
        },
        { key: 'comment', isPrimary: true },
        'lastModified',
        'distributions',
        'id',
        'type',
      ],
      policy: null,
      hasPermissions: true,
    }
  },
  computed: {
    requestPolicies() {
      return this.$store.state.requestPolicies.requestPolicies
    },
    loading() {
      return this.$store.state.requestPolicies.fetchingRequestPolicies
    },
    isSelected() {
      return this.selected[0]
    },
    isEditable() {
      if (this.selected[0]) {
        return this.selected[0].isGjirafaPolicy
      }
      return true
    },
    removingRequestPolicy() {
      return this.$store.state.requestPolicies.removingRequestPolicy
    },
  },
  created() {
    this.id = this.$route.params.id
    this.$store
      .dispatch('requestPolicies/fetchRequestPolicies')
      .catch(error => {
        if (error.response?.status === 403) {
          this.hasPermissions = false
          return
        }
        this.errorToast(
          error.response?.data?.errors[0]
            || 'Something went wrong while fetching request policies.',
        )
      })
  },
  methods: {
    select(policy) {
      this.selected = policy !== null ? [policy] : []
    },
    onRowSelected(item) {
      this.selected = item
    },
    onEditClick() {
      if (this.isSelected) {
        this.$router.push({
          name: 'request-policy-edit',
          params: { requestId: this.selected[0].id },
        })
      }
    },
    onDeleteClick() {
      this.confirmDelete(
        'Delete Request Policy',
        'Are you sure you want to delete this request policy?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch(
              'requestPolicies/removeRequestPolicy',
              this.selected[0].id,
            )
            .then(() => {
              this.successToast('Request policy was removed')
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0] || 'Something went wrong.',
              )
            })
        }
      })
    },
    onNameClick(requestId) {
      this.policy = requestId
      this.$router.push({
        name: 'request-policy-info',
        params: { requestId },
        query: { v: 'info' },
      })
    },
    showInformation(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
  },
}
</script>
